import { type SelectedVariant } from "@tablescape/state";
import {
  getProductHandleFromUrl,
  getVariantMetadata,
} from "@tablescape/utilities/product";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import {
  type LanguageCode,
  type CountryCode,
} from "@shopify/hydrogen-react/storefront-api-types";

const abortController = new AbortController();

type VariantsCache = {
  [productHandle: string]: {
    [optionName: string]: {
      [optionValue: string]: string;
    };
  };
};

const loaderClasses = [
  "h-screen",
  "w-screen",
  "bg-background-primary",
  "opacity-40",
  "relative",
  "transition-all",
];

export class ProductBridge {
  private currentVariant: SelectedVariant | undefined = undefined;
  private url: URL = new URL(window.location.origin);
  private variantsCache: VariantsCache = {};
  private spinner: HTMLElement | undefined;

  constructor() {
    TablescapeContext.on(TablescapeEvent.VARIANT_SELECTED, (data?) => {
      const currentProductHandle = getProductHandleFromUrl(
        window.location.pathname
      );
      if (!data || !data.products || !data.products[currentProductHandle])
        return;
      this.currentVariant = data.products[currentProductHandle].selectedVariant;
    });

    window.addEventListener("popstate", () => {
      const currentProductHandle = getProductHandleFromUrl(
        window.location.pathname
      );

      const searchParams = new URLSearchParams(window.location.search);
      const variantId = searchParams.get("variant") ?? undefined;
      this.push(currentProductHandle, variantId, true);
    });
  }

  private async getMatchingProductVariant(
    country: CountryCode,
    language: LanguageCode,
    productHandle: string,
    variantId?: string
  ) {
    if (!this.currentVariant) return;
    const { selectedOptions } = this.currentVariant;
    const variant = await getVariantMetadata(
      {
        country,
        language,
        productHandle,
        selectedOptions,
        variantId,
      },
      {
        abortController,
      }
    );

    return variant;
  }

  private async fetchProductSections(
    productHandle: string,
    variantId?: string
  ) {
    try {
      if (!this.currentVariant) return;
      const { selectedOptions } = this.currentVariant;
      let htmlText =
        this.variantsCache[productHandle]?.[selectedOptions[0].name]?.[
          selectedOptions[0].value
        ];

      if (!htmlText) {
        this.url.pathname =
          window.Shopify?.routes?.root + `products/${productHandle}`;
        const country = document.documentElement.dataset
          ?.countryCode as CountryCode;
        const language = document.documentElement.dataset
          ?.languageCode as LanguageCode;
        const matchingVariant = await this.getMatchingProductVariant(
          country,
          language,
          productHandle,
          variantId
        );

        if (matchingVariant) {
          this.url.searchParams.set("variant", matchingVariant.id);
        } else {
          this.url.searchParams.delete("variant");
        }

        const response = await fetch(this.url.href);
        htmlText = await response.text();

        if (matchingVariant) {
          this.variantsCache[productHandle] = {
            ...this.variantsCache[productHandle],
            [matchingVariant.selectedOptions[0].name]: {
              ...this.variantsCache[productHandle]?.[
                matchingVariant.selectedOptions[0].name
              ],
              [matchingVariant.selectedOptions[0].value]: htmlText,
            },
          };
        }
      }

      const productHTML = new DOMParser().parseFromString(
        htmlText,
        "text/html"
      );
      return productHTML.querySelector<HTMLElement>("main");
    } catch (error) {
      console.error(error);
    }
  }

  private addLoaders(element: HTMLElement) {
    const domParser = new DOMParser();
    this.spinner = domParser.parseFromString(
      `
    <div class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="animate-spin">
        <path d="M45.5 24C45.5 28.2523 44.239 32.4091 41.8766 35.9448C39.5141 39.4804 36.1563 42.2361 32.2277 43.8634C28.2991 45.4907 23.9761 45.9165 19.8056 45.0869C15.635 44.2573 11.804 42.2096 8.7972 39.2028C5.79038 36.196 3.7427 32.365 2.91312 28.1944C2.08353 24.0239 2.50931 19.7009 4.13659 15.7723C5.76387 11.8437 8.51958 8.48585 12.0552 6.1234C15.5909 3.76095 19.7477 2.5 24 2.5" stroke="url(#paint0_angular_1852_25677)" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
        <defs>
        <radialGradient id="paint0_angular_1852_25677" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(24 24) scale(24)">
        <stop offset="0.75" stop-color="#35312E"/>
        <stop offset="0.977308" stop-color="#35312E" stop-opacity="0"/>
        </radialGradient>
        </defs>
      </svg>
    </div>
    `,
      "text/html"
    ).documentElement;
    element.classList.add(...loaderClasses);
    element.append(this.spinner);
  }

  private removeLoaders(element: HTMLElement) {
    element.classList.remove(...loaderClasses);
    if (this.spinner) {
      this.spinner.remove();
    }
  }

  private dispatchShopifySectionLoadEvents(rootNode: HTMLElement) {
    const sectionIds = Array.from(
      rootNode.querySelectorAll(`[id^="shopify-section-"]`)
    ).map((section) => section.id.replace("shopify-section-", ""));

    sectionIds.forEach((sectionId) => {
      window.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          detail: {
            sectionId,
          },
        })
      );
    });
  }

  public destroy() {
    this.currentVariant = undefined;
    this.variantsCache = {};
  }

  public async push(
    productHandle: string,
    variantId?: string,
    skipPushState = false
  ) {
    const main = document.querySelector("main");
    if (!main) return;

    try {
      this.addLoaders(main);
      const templateSections = await this.fetchProductSections(
        productHandle,
        variantId
      );
      if (!templateSections) {
        this.removeLoaders(main);
        return;
      }
      main.innerHTML = templateSections.innerHTML;
      if (!skipPushState) {
        history.pushState({}, "", this.url);
      }

      this.dispatchShopifySectionLoadEvents(templateSections);
      this.removeLoaders(main);
    } catch (e) {
      this.removeLoaders(main);
      console.error(e);
    }
  }
}

export const ProductURL = new ProductBridge();
